import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import { siteConfig } from '@/siteConfig';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

export const About = () => {
  const { language, t } = useLanguage();

  useEffect(() => {
    document.title = language === 'fr' ? 'À Propos | NgSys Automatisation' : 'About | NgSys Automation';
  }, [language]);

  return (
    <>
      <div className="flex flex-col">
        {/* Hero */}
        <section className="py-20 md:py-32 bg-gradient-to-br from-primary/10 via-background to-accent/5">
          <div className="container mx-auto px-4 md:px-8 lg:px-12 text-center">
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6" data-testid="about-title">
              {language === 'fr' ? 'À Propos de Nous' : 'About Us'}
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {t(siteConfig.brand.tagline)}
            </p>
          </div>
        </section>

        {/* Mission / Vision */}
        <section className="py-20" data-testid="mission-vision-section">
          <div className="container mx-auto px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="mb-12"
              >
                <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                  {language === 'fr' ? 'Notre Mission & Vision' : 'Our Mission & Vision'}
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {t(siteConfig.vision)}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <h3 className="font-heading text-2xl font-semibold mb-6">
                  {language === 'fr' ? 'Couverture de Service' : 'Service Coverage'}
                </h3>
                <p className="text-lg text-muted-foreground flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                  {t(siteConfig.brand.serviceArea)}
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 md:py-32 bg-muted/30" data-testid="values-section">
          <div className="container mx-auto px-4 md:px-8 lg:px-12">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                {language === 'fr' ? 'Nos Valeurs' : 'Our Values'}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {siteConfig.values[language].map((value, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  data-testid={`value-card-${idx}`}
                >
                  <Card className="h-full hover:border-primary/50 transition-colors duration-300">
                    <CardContent className="p-6 text-center">
                      <h3 className="font-heading text-lg font-semibold mb-2 text-primary">
                        {value.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">{value.desc}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 md:py-32 bg-primary text-primary-foreground" data-testid="about-cta-section">
          <div className="container mx-auto px-4 md:px-8 lg:px-12 text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              {language === 'fr' ? 'Travaillons ensemble' : "Let's Work Together"}
            </h2>
            <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
              {language === 'fr'
                ? 'Contactez-nous pour discuter de vos besoins en automatisation industrielle.'
                : 'Contact us to discuss your industrial automation needs.'}
            </p>
            <Button asChild size="lg" variant="secondary" className="font-bold uppercase tracking-wide">
              <Link to="/contact">{t(siteConfig.brand.primaryCTA)}</Link>
            </Button>
          </div>
        </section>
      </div>
    </>
  );
};